import { Button } from '@/components/ui/Button';
import { parseLocalDate } from '@/utils';
import { formatDateForInput } from '@/utils/dateUtils';
import type { Database } from '@/types/database.types';

type Milestone = Database['public']['Tables']['milestones']['Row'];
type MilestoneType = Milestone['type'];

interface MilestoneListProps {
  /** Array of milestones to display */
  milestones: Milestone[];
  /** Callback when edit button is clicked */
  onEdit: (milestone: Milestone) => void;
  /** Callback when delete button is clicked */
  onDelete: (milestone: Milestone) => void;
  /** Whether loading state is active */
  isLoading?: boolean;
}

const MILESTONE_TYPE_COLORS: Record<MilestoneType, { bg: string; text: string }> = {
  project_milestone: { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  external_dependency: { bg: 'bg-warning-100', text: 'text-warning-800' },
  delivery_gate: { bg: 'bg-green-100', text: 'text-green-800' },
};

const MILESTONE_TYPE_LABELS: Record<MilestoneType, string> = {
  project_milestone: 'Project Milestone',
  external_dependency: 'External Dependency',
  delivery_gate: 'Delivery Gate',
};

const FALLBACK_COLORS = { bg: 'bg-gray-100', text: 'text-gray-800' };

/**
 * MilestoneList component displays a list of project milestones
 * Supports edit and delete operations
 */
export const MilestoneList = ({
  milestones,
  onEdit,
  onDelete,
  isLoading = false,
}: MilestoneListProps) => {
  if (milestones.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No milestones yet. Create one to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {milestones.map((milestone) => {
        const typeColors = MILESTONE_TYPE_COLORS[milestone.type] ?? FALLBACK_COLORS;
        const typeLabel = MILESTONE_TYPE_LABELS[milestone.type] ?? milestone.type;

        return (
          <div
            key={milestone.id}
            className="flex items-start justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-gray-900 truncate">{milestone.name}</h4>
                <span
                  className={`inline-flex text-xs font-semibold px-2 py-1 rounded-full ${typeColors.bg} ${typeColors.text}`}
                >
                  {typeLabel}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Target:{' '}
                <span className="font-medium">
                  {formatDateForInput(parseLocalDate(milestone.target_date))}
                </span>
              </p>
              {milestone.description && (
                <p className="text-sm text-gray-600 truncate">{milestone.description}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 ml-4 flex-shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEdit(milestone)}
                disabled={isLoading}
                aria-label={`Edit ${milestone.name}`}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onDelete(milestone)}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label={`Delete ${milestone.name}`}
              >
                Delete
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
